///////////////////////////////////////////////////////////////////////////////
// Voice search — which locale to hand the speech recogniser.
//
// Browser and native recognisers cannot auto-detect language: every session
// needs a BCP-47 locale, and the wrong one returns garbage. Only the parse
// step auto-detects (from the transcript). So the recogniser locale comes
// from: the customer's own last choice → the site language → the browser
// language → English. Plain JS (no DOM) so it is unit-testable.
///////////////////////////////////////////////////////////////////////////////

// Recogniser locales offered in the chip. Ordered by who buys here
// (Filipino seafarers on Thai data first — see docs/PRD.md), then the rest
// of the site's languages, then the markets the catalog serves.
export const VOICE_LOCALES = [
  { code: 'en-US', label: 'English' },
  { code: 'fil-PH', label: 'Filipino' },
  { code: 'th-TH', label: 'ไทย' },
  { code: 'my-MM', label: 'မြန်မာ' },
  { code: 'id-ID', label: 'Bahasa Indonesia' },
  { code: 'vi-VN', label: 'Tiếng Việt' },
  { code: 'zh-CN', label: '中文' },
  { code: 'ja-JP', label: '日本語' },
  { code: 'ko-KR', label: '한국어' },
  { code: 'es-419', label: 'Español' },
  { code: 'ru-RU', label: 'Русский' },
  { code: 'hi-IN', label: 'हिन्दी' },
  { code: 'ms-MY', label: 'Bahasa Melayu' },
  { code: 'pt-BR', label: 'Português' },
  { code: 'fr-FR', label: 'Français' },
  { code: 'ar-SA', label: 'العربية' },
  { code: 'km-KH', label: 'ខ្មែរ' },
  { code: 'bn-BD', label: 'বাংলা' },
  { code: 'ne-NP', label: 'नेपाली' },
  { code: 'ur-PK', label: 'اردو' },
];

// Site language codes (client/web/Chrome.jsx LANGS, legacy cn/jp) → recogniser locale
const SITE_LANG_TO_LOCALE = { en: 'en-US', th: 'th-TH', ru: 'ru-RU', cn: 'zh-CN', es: 'es-419', jp: 'ja-JP', ko: 'ko-KR' };

export const VOICE_LANG_KEY = 'mt_voice_lang';

const known = (code) => VOICE_LOCALES.some((l) => l.code === code);

// A browser language like "fil", "tl-PH", "es-MX" or "zh-Hans-CN" → the
// closest offered locale, or null.
export function matchLocale(tag) {
  if (!tag) return null;
  const t = String(tag).replace('_', '-');
  if (known(t)) return t;
  const lang = t.split('-')[0].toLowerCase();
  if (lang === 'tl') return 'fil-PH';
  if (lang === 'zh') return 'zh-CN';
  const hit = VOICE_LOCALES.find((l) => l.code.split('-')[0] === lang);
  return hit ? hit.code : null;
}

/**
 * pickVoiceLocale({ stored, siteLang, browserLang }) → locale code.
 * `stored` is the customer's own last choice and always wins when offered.
 */
export function pickVoiceLocale({ stored, siteLang, browserLang } = {}) {
  if (stored && known(stored)) return stored;
  const site = SITE_LANG_TO_LOCALE[siteLang];
  // The site language is a real signal only when it isn't the default: a
  // Filipino customer on the English site should get their phone's language.
  if (site && siteLang !== 'en') return site;
  const fromBrowser = matchLocale(browserLang);
  if (fromBrowser) return fromBrowser;
  return site || 'en-US';
}
